import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useRef, useState, type ChangeEvent } from 'react';
import { Controller, type Control } from 'react-hook-form';

import { useFileUpload } from '@/api/hooks';
import ImageCropModal from '@/components/AvatarUploader/ImageCropModal';
import { CristaSpinner } from '@/components/Loaders';
import { type ModalRef } from '@/components/Modal';
import { IconPhotoPlus } from '@/components/icon/tabler/IconPhotoPlus';
import { IconX } from '@/components/icon/tabler/IconX';
import { type OfferFormValues } from '@/domain/offers/types';
import { useTranslate } from '@/state/i18n';
import { validateImageCropFile } from '@/utils/image';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  control: Control<OfferFormValues>;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

/** P18 image: 16:9 crop uploaded to S3 under `partners/`, same flow as the event banner. */
const OfferImageField = ({ control }: Props) => {
  const t = useTranslate('domain.offers.components.OfferImageField');
  const { enqueueSnackbar } = useSnackbar();
  const { uploadFileWithPresignedUrl, isLoading } = useFileUpload({ folder: 'partners' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropModalRef = useRef<ModalRef>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);

  const selectFile = (file?: File) => {
    if (!file) return;
    const error = validateImageCropFile(file, {
      acceptedMimeTypes: ACCEPTED_TYPES,
      maxFileSizeBytes: MAX_FILE_SIZE,
      invalidFileMessage: t('invalidImage'),
    });
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
      return;
    }
    setCropFile(file);
    cropModalRef.current?.open();
  };

  return (
    <Controller
      name="imageUrl"
      control={control}
      render={({ field }) => (
        <Box>
          <Box
            role="button"
            tabIndex={0}
            aria-label={field.value ? t('replace') : t('add')}
            onClick={() => !isLoading && fileInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') fileInputRef.current?.click();
            }}
            sx={sx.dropzone}
          >
            {field.value ? (
              <>
                <Box component="img" src={field.value} alt={t('alt')} sx={sx.preview} />
                <IconButton
                  size="small"
                  aria-label={t('remove')}
                  color="rounded-error"
                  sx={sx.remove}
                  onClick={(event) => {
                    event.stopPropagation();
                    field.onChange('');
                  }}
                >
                  <IconX fontSize="small" />
                </IconButton>
              </>
            ) : isLoading ? (
              <CristaSpinner size={24} />
            ) : (
              <Stack alignItems="center" gap={0.5}>
                <IconPhotoPlus sx={{ fontSize: 32, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {t('add')}
                </Typography>
              </Stack>
            )}
          </Box>
          <FormHelperText>{t('hint')}</FormHelperText>
          <Box
            component="input"
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            hidden
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              selectFile(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <ImageCropModal
            ref={cropModalRef}
            file={cropFile}
            onConfirm={async (file) => {
              try {
                field.onChange(await uploadFileWithPresignedUrl(file));
              } catch {
                // useFileUpload already shows the error.
              }
            }}
            onDismiss={() => setCropFile(null)}
            cropModalTitle={t('cropTitle')}
            cropShape="rect"
            cropAspect={16 / 9}
            maxOutputSize={1600}
            quality={0.82}
            outputMimeType="image/webp"
          />
        </Box>
      )}
    />
  );
};

export default OfferImageField;

const sx = {
  dropzone: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    maxHeight: 280,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px dashed',
    borderColor: 'divider',
    borderRadius: radiusTokens.xl,
    overflow: 'hidden',
    cursor: 'pointer',
    bgcolor: 'action.hover',
    '&:hover, &:focus-visible': { borderColor: 'text.secondary' },
  },
  preview: { width: '100%', height: '100%', objectFit: 'cover' },
  remove: { position: 'absolute', top: 8, right: 8 },
};
